import { AlertTriangle, RefreshCw, SearchX } from 'lucide-react';
import { Button } from '@ncoder/ui';
import { cn } from '@/lib/utils';

/**
 * The admin panel's empty / error / loading states.
 *
 * Every list in the module previously hand-rolled its own three states, which produced: four
 * bespoke skeleton components whose shapes did not match the rows they stood in for, three empty
 * states whose icon circles were tinted three different colours for no semantic reason, and — the
 * real defect — NO error state at all. `useOrgMembers().isError` was never read, so a 500 from the
 * members endpoint rendered "No members yet.", telling an admin their org is empty when the request
 * simply failed. These are the one implementation of all three.
 */

export interface OrgAdminEmptyStateProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  /** The action that resolves the emptiness. An empty state without one is a dead end. */
  action?: React.ReactNode;
  className?: string;
}

export function OrgAdminEmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: OrgAdminEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex h-full min-h-[220px] flex-col items-center justify-center px-6 py-10 text-center',
        className,
      )}
    >
      {/* One neutral treatment for every empty state in the module — the icon identifies the
          section, it is not a status, so it does not get a status colour. */}
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-border/60 bg-muted/40">
        <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
      </div>
      <p className="oa-title">{title}</p>
      {description && <p className="oa-meta mt-1.5 max-w-[42ch]">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export interface OrgAdminErrorStateProps {
  title?: string;
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

function messageOf(error: unknown, fallback: string): string {
  const message = (error as Error | undefined)?.message;
  return message && message.trim() ? message : fallback;
}

export function OrgAdminErrorState({
  title = "That didn't load",
  error,
  onRetry,
  className,
}: OrgAdminErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex h-full min-h-[220px] flex-col items-center justify-center px-6 py-10 text-center',
        className,
      )}
    >
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-destructive/25 bg-destructive/10">
        <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden />
      </div>
      <p className="oa-title">{title}</p>
      <p className="oa-meta mt-1.5 max-w-[52ch] break-words">
        {messageOf(error, 'The request failed. This is usually temporary.')}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Try again
        </Button>
      )}
    </div>
  );
}

/** Shown when a filter/search excludes every row — distinct from "there is nothing here yet". */
export function OrgAdminNoResults({
  query,
  onClear,
  className,
}: {
  query: string;
  onClear: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-full min-h-[180px] flex-col items-center justify-center px-6 py-10 text-center',
        className,
      )}
    >
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-border/60 bg-muted/40">
        <SearchX className="h-5 w-5 text-muted-foreground" aria-hidden />
      </div>
      <p className="oa-title">No matches</p>
      <p className="oa-meta mt-1.5 max-w-[42ch]">
        {query ? <>Nothing matches “{query}”.</> : 'Nothing matches the current filters.'}
      </p>
      <Button variant="ghost" size="sm" className="mt-3" onClick={onClear}>
        Clear filters
      </Button>
    </div>
  );
}

/**
 * A loading placeholder shaped like the rows it stands in for, announced as busy.
 *
 * `role="status"` + `aria-busy` matter here: the old skeletons were decorative divs, so a screen
 * reader user heard silence between navigating to a tab and the data arriving, with no way to tell
 * whether the page was loading or empty.
 */
export function OrgAdminTableSkeleton({
  rows = 6,
  columns,
  label = 'Loading…',
}: {
  rows?: number;
  /** Relative column widths, e.g. `['40%', '20%', '20%', '20%']`. */
  columns: readonly string[];
  label?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className="px-[var(--oa-gutter)]">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex items-center gap-3 border-b border-border/30 py-3 last:border-b-0"
          style={{ height: 'var(--oa-row-h)' }}
        >
          {columns.map((width, columnIndex) => (
            <div
              key={columnIndex}
              className="oa-skeleton h-3"
              style={{
                width,
                // A uniform grey grid reads as a broken table; a slight per-row variance reads as
                // content that has not arrived. Deterministic, so screenshots stay comparable.
                opacity: 1 - ((rowIndex + columnIndex) % 3) * 0.12,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function OrgAdminCardSkeleton({ cards = 6 }: { cards?: number }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading roles…"
      className="grid gap-3 p-[var(--oa-gutter)] [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]"
    >
      <span className="sr-only">Loading roles…</span>
      {Array.from({ length: cards }).map((_, index) => (
        <div key={index} className="oa-raised flex flex-col gap-3 p-4">
          <div className="oa-skeleton h-3.5 w-28" />
          <div className="oa-skeleton h-2.5 w-16" />
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: (index % 3) + 1 }).map((_, chip) => (
              <div key={chip} className="oa-skeleton h-5 w-16 rounded-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
